import React, { useMemo } from 'react';
import { Text, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { DisplayScreen } from '@oolio-group/domain';
import { Media, hasRenderableMedia } from '../../../component/Media/Media';
import { BrandMark } from '../../../component/BrandMark/BrandMark';
import { Attribution } from '../../../component/Attribution/Attribution';
import { useTheme } from '../../../hooks/useTheme';
import animations from '../../../common/animations';
import { resolveBrandMark } from './resolveBrandMark';
import { resolveAttributionMode } from './resolveAttributionMode';
import { getStyles } from './MediaSection.styles';

interface Props {
  config?: DisplayScreen;
  brandColor?: string;
  customerName?: string;
  storeName?: string;
  showAttribution?: boolean;
}

const MediaSection: React.FC<Props> = ({
  config,
  brandColor,
  customerName,
  storeName,
  showAttribution,
}) => {
  const { colors } = useTheme();
  const fallbackBackground = brandColor ?? colors.elevation0;
  const styles = useMemo(
    () => getStyles(colors, fallbackBackground),
    [colors, fallbackBackground],
  );

  const hasMedia = hasRenderableMedia(config);
  const brandMark = resolveBrandMark(config, hasMedia, storeName);

  return (
    <View style={[styles.rhs, !hasMedia && styles.rhsFallback]}>
      {hasMedia && <Media config={config} />}
      {brandMark && (
        <View style={styles.brandMark} pointerEvents="none">
          <BrandMark
            logoUri={brandMark.logoUri}
            name={brandMark.name}
            backgroundColor={brandColor}
            logoStyle={styles.logo}
          />
        </View>
      )}
      {showAttribution && (
        <Attribution
          mode={resolveAttributionMode(hasMedia, fallbackBackground)}
          containerStyles={styles.attribution}
        />
      )}
      {customerName && (
        <Animated.View
          style={styles.bubble}
          entering={animations.slideInRight}
          exiting={animations.slideOutRight}
        >
          <Text style={styles.bubbleText}>Hello {customerName} 👋</Text>
        </Animated.View>
      )}
    </View>
  );
};

export default React.memo(MediaSection);
